import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FileText, ChevronDown, ChevronUp, Mail, ArrowLeft } from "lucide-react";
import logo from "./assets/imgiconlogo.png";

const EFFECTIVE_DATE = "13 June 2026";
const COMPANY = "TinyGlobal Pty Ltd";
const ABN = "42 327 513 086";
const CONTACT_EMAIL = "contact@yadata.com.au";
const WEBSITE = "tinyglobalop.com";

const sections = [
  {
    id: "acceptance",
    title: "1. Acceptance of Terms",
    content: `These Terms of Service ("Terms") constitute a legally binding agreement between you ("User", "you", "your") and ${COMPANY} (ABN ${ABN}) ("TinyGlobal", "we", "us", "our"), the operator of the logistics operating platform at ${WEBSITE} and related subdomains (the "Platform").

By registering for an account, accessing the Platform, or using any of our services, you agree to be bound by these Terms and our Privacy Policy. If you are accepting these Terms on behalf of a company or other legal entity, you represent that you have the authority to bind that entity.

If you do not agree to these Terms, you must not access or use the Platform.

These Terms are governed by the laws of New South Wales, Australia, and the parties submit to the exclusive jurisdiction of the courts of New South Wales.`,
  },
  {
    id: "service",
    title: "2. Description of Service",
    content: `TinyGlobal is a cloud-based Software-as-a-Service (SaaS) logistics operating system designed for Australian import and wholesale businesses. The Platform provides, among other features:

• Inbound planning, purchase order management, and ASN processing
• Supplier collaboration portal and EDI connectivity (AS2, VAN, REST API, SFTP, CSV)
• Multi-warehouse inventory management and WMS functionality
• Customs entry management and ATT accreditation workflow support
• HS code evaluation and trade compliance tools
• Subscription and payments management
• Reporting and operational analytics

We reserve the right to modify, suspend, or discontinue any feature or aspect of the Platform at any time, with reasonable notice where practicable. We are not liable to you or any third party for any modification, suspension, or discontinuation of the Platform or any feature thereof.`,
  },
  {
    id: "accounts",
    title: "3. Account Registration & Access",
    content: `**Eligibility**
You must be at least 18 years of age and authorised to enter into legally binding contracts under Australian law to register for a TinyGlobal account.

**Registration**
You agree to provide accurate, current, and complete information during registration and to update such information as necessary to keep it accurate and complete.

**Account Security**
You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately at ${CONTACT_EMAIL} if you suspect any unauthorised access to your account.

**Multi-Tenant Structure**
The Platform uses a multi-tenant architecture. Each organisation ("Tenant") operates within an isolated data environment. Tenant administrators are responsible for managing user roles and permissions within their organisation. You must not attempt to access data belonging to another Tenant.

**Roles & Permissions**
Access to features is controlled by role (Client, Operator, Agent, 3PL, Admin, Super-Admin). You must use the Platform only within the scope of your assigned role.`,
  },
  {
    id: "acceptable-use",
    title: "4. Acceptable Use",
    content: `You agree to use the Platform only for lawful purposes and in accordance with these Terms. You must not:

• Use the Platform in any way that violates any applicable Commonwealth, State, or Territory law, or any international laws or regulations
• Submit false, misleading, or fraudulent customs declarations, HS code evaluations, or ATT application data
• Attempt to gain unauthorised access to the Platform, other user accounts, or our servers or networks
• Introduce viruses, malware, or other malicious code to the Platform
• Use automated scripts, bots, or data scraping tools against the Platform without our express written consent
• Reverse engineer, decompile, or disassemble any part of the Platform
• Resell, sublicense, or commercially exploit the Platform or any data derived from it without our written authorisation
• Use the Platform to transmit unsolicited commercial communications in violation of the Spam Act 2003 (Cth)
• Interfere with or disrupt the integrity or performance of the Platform or its underlying infrastructure

We may suspend or terminate your access immediately and without notice if we reasonably believe you have violated these acceptable use obligations.`,
  },
  {
    id: "subscriptions",
    title: "5. Subscriptions, Fees & Payment",
    content: `**Subscription Plans**
Access to the Platform is provided on a subscription basis. Plan details, features, and pricing are as set out on our website or in your order form. We reserve the right to change pricing with 30 days' notice.

**Billing**
Subscription fees are billed in advance, monthly or annually depending on your plan. All fees are in Australian Dollars (AUD) and are inclusive of GST unless stated otherwise.

**Payment Processing**
Payments are processed through Square Inc., a third-party payment processor. By providing payment details, you authorise us to charge the applicable fees to your nominated payment method.

**Late Payment**
If a payment fails and is not remedied within 7 days, we may suspend your access to the Platform until the outstanding amount is resolved.

**Refunds**
Subscription fees are non-refundable except where required by the Australian Consumer Law (Schedule 2 of the Competition and Consumer Act 2010 (Cth)) or as otherwise agreed in writing.

**Australian Consumer Law**
Nothing in these Terms excludes, restricts, or modifies any consumer guarantee, right, or remedy under the Australian Consumer Law that cannot be excluded, restricted, or modified by agreement.`,
  },
  {
    id: "data",
    title: "6. Your Data",
    content: `**Ownership**
You retain full ownership of all data, documents, and information you upload or create within the Platform ("Customer Data"). We claim no intellectual property rights over Customer Data.

**Licence to Us**
You grant TinyGlobal a limited, non-exclusive, royalty-free licence to process, store, and display Customer Data solely for the purpose of providing the Platform and related services to you.

**Data Security**
We implement reasonable technical and organisational measures to protect Customer Data against unauthorised access, loss, or disclosure, as described in our Privacy Policy.

**Data Portability & Export**
You may export your Customer Data at any time during your subscription using the export tools available within the Platform.

**Retention on Termination**
Following termination of your subscription, we will retain Customer Data for 90 days to allow you to request an export, after which it will be securely deleted unless we are required to retain it by law (e.g. for customs audit purposes).

**Customs & Compliance Data**
You acknowledge that certain data entered into the Platform (including customs entries, HS code evaluations, and ATT application data) may be subject to retention and disclosure obligations under Australian customs legislation, and that we may be required to retain or disclose such data independent of your instructions.`,
  },
  {
    id: "ip",
    title: "7. Intellectual Property",
    content: `**TinyGlobal IP**
All intellectual property rights in and to the Platform, including its software, design, code, trademarks, logos, documentation, and underlying technology, are owned by or licensed to ${COMPANY} and its related entity Yadata Pty Ltd. Nothing in these Terms transfers any such rights to you.

**Licence to Use**
Subject to your compliance with these Terms and payment of applicable fees, we grant you a limited, non-exclusive, non-transferable, revocable licence to access and use the Platform solely for your internal business operations during the term of your subscription.

**Feedback**
If you provide feedback, suggestions, or ideas about the Platform ("Feedback"), you grant us a perpetual, irrevocable, worldwide, royalty-free licence to use, incorporate, and commercialise that Feedback without any obligation to you.`,
  },
  {
    id: "confidentiality",
    title: "8. Confidentiality",
    content: `Each party agrees to keep confidential any non-public information disclosed by the other party in connection with the Platform that is designated as confidential or that reasonably should be understood to be confidential ("Confidential Information").

Each party agrees to:
• Use Confidential Information only to exercise its rights and fulfil its obligations under these Terms
• Protect Confidential Information using at least the same degree of care it uses for its own confidential information (and no less than reasonable care)
• Not disclose Confidential Information to third parties without the other party's prior written consent, except to employees or contractors who need to know it and are bound by confidentiality obligations at least as restrictive as these Terms

This obligation does not apply to information that: (a) is or becomes publicly available through no breach of these Terms; (b) was independently developed without use of the Confidential Information; or (c) is required to be disclosed by law, provided the disclosing party gives reasonable prior notice.`,
  },
  {
    id: "availability",
    title: "9. Service Availability & Support",
    content: `**Availability**
We aim to provide high availability of the Platform but do not guarantee uninterrupted or error-free service. Scheduled maintenance will be communicated in advance where practicable. Unplanned outages will be addressed promptly.

**Support**
Support is provided via the in-Platform ticketing system and by email. Response times vary by subscription plan as detailed on our website.

**Third-Party Integrations**
The Platform connects with third-party services including the Australian Border Force (ABF) systems, ERP providers, VAN networks, and payment processors. We are not responsible for the availability, accuracy, or performance of third-party systems or for delays caused by such systems.`,
  },
  {
    id: "liability",
    title: "10. Limitation of Liability",
    content: `**To the fullest extent permitted by applicable law (including the Australian Consumer Law):**

• TinyGlobal's total cumulative liability to you for any claims arising under or in connection with these Terms or the Platform — whether in contract, tort (including negligence), statute, or otherwise — will not exceed the total subscription fees paid by you to TinyGlobal in the 12 months immediately preceding the event giving rise to the claim.

• We will not be liable for any: (a) indirect, incidental, consequential, special, or punitive damages; (b) loss of profits, revenue, business, goodwill, or data; (c) customs delays, examinations, penalties, or duty assessments arising from data you entered into the Platform; (d) losses resulting from third-party system failures, government actions, or force majeure events.

**Australian Consumer Law**
If you are a consumer under the Australian Consumer Law, our services come with guarantees that cannot be excluded. Where our liability can be limited, it is limited to: re-supplying the services; or paying the cost of having the services supplied again.

**Customs Disclaimer**
TinyGlobal provides tools to assist with customs and trade compliance workflows. It does not constitute legal or customs advice. You are solely responsible for the accuracy of all declarations, HS code classifications, and ATT application submissions. We recommend you engage a licensed customs broker for complex compliance matters.`,
  },
  {
    id: "indemnity",
    title: "11. Indemnification",
    content: `You agree to indemnify, defend, and hold harmless TinyGlobal, its related entities (including Yadata Pty Ltd), officers, directors, employees, and agents from and against any claims, damages, losses, liabilities, costs, and expenses (including reasonable legal fees) arising out of or relating to:

• Your use of the Platform in violation of these Terms
• Customer Data you submit to the Platform, including inaccurate customs declarations, incorrect HS code classifications, or fraudulent documentation
• Your violation of any applicable law or third-party rights
• Any dispute between you and a supplier, 3PL, or other third party facilitated through the Platform`,
  },
  {
    id: "termination",
    title: "12. Termination",
    content: `**Termination by You**
You may cancel your subscription at any time through the Platform settings or by contacting us at ${CONTACT_EMAIL}. Cancellation takes effect at the end of the current billing period. No refund is provided for the remainder of any prepaid period unless required by the Australian Consumer Law.

**Termination by Us**
We may suspend or terminate your access to the Platform immediately and without notice if:
• You materially breach these Terms and (where the breach is capable of remedy) fail to remedy it within 14 days of written notice
• You fail to pay subscription fees when due and do not remedy the non-payment within 7 days
• We reasonably believe your use of the Platform creates a legal, security, or reputational risk to TinyGlobal or other users
• You become insolvent, enter administration, or cease to carry on business

**Effect of Termination**
On termination: (a) your access to the Platform will cease; (b) we will retain Customer Data for 90 days before deletion as described in clause 6; (c) provisions of these Terms that by their nature should survive termination will continue in effect, including clauses 7, 8, 10, 11, and 14.`,
  },
  {
    id: "governing-law",
    title: "13. Governing Law & Dispute Resolution",
    content: `These Terms are governed by and construed in accordance with the laws of New South Wales, Australia, without regard to its conflict of law principles.

**Dispute Resolution Process**
If a dispute arises in connection with these Terms, the parties agree to:

1. **Negotiation** — Contact the other party in writing and attempt to resolve the dispute in good faith within 14 days
2. **Mediation** — If negotiation fails, refer the dispute to mediation administered by the Australian Disputes Centre (ADC) in Sydney, NSW
3. **Litigation** — If mediation fails, either party may commence proceedings in the courts of New South Wales

Nothing in this clause prevents either party from seeking urgent injunctive or interlocutory relief.

The United Nations Convention on Contracts for the International Sale of Goods does not apply to these Terms.`,
  },
  {
    id: "general",
    title: "14. General",
    content: `**Entire Agreement**
These Terms and our Privacy Policy constitute the entire agreement between you and TinyGlobal regarding the Platform and supersede all prior agreements, representations, and understandings.

**Amendments**
We may update these Terms from time to time. We will notify you of material changes by posting the updated Terms with a new effective date and, where appropriate, by in-Platform notification or email. Continued use of the Platform after updated Terms take effect constitutes acceptance of the new Terms.

**Severability**
If any provision of these Terms is found to be invalid, illegal, or unenforceable, that provision will be modified to the minimum extent necessary to make it enforceable, and the remaining provisions will continue in full force and effect.

**Waiver**
Our failure to enforce any right or provision of these Terms does not constitute a waiver of that right or provision.

**Assignment**
You may not assign your rights or obligations under these Terms without our prior written consent. We may assign our rights and obligations to a related entity or in connection with a merger, acquisition, or sale of assets.

**Force Majeure**
We are not liable for any failure or delay in performance resulting from circumstances beyond our reasonable control, including natural disasters, government actions, telecommunications failures, or cyberattacks.

**Notices**
Notices to us should be sent to ${CONTACT_EMAIL}. Notices to you will be sent to the email address registered on your account.`,
  },
  {
    id: "contact",
    title: "15. Contact",
    content: `For any questions about these Terms:

**${COMPANY}**
ABN: ${ABN}
Sydney, New South Wales, Australia

**Operated by Yadata Pty Ltd**
Email: ${CONTACT_EMAIL}
Website: www.yadata.com.au`,
  },
];

function Section({ s }) {
  const [open, setOpen] = useState(true);

  const renderContent = (text) => {
    return text.split("\n").map((line, i) => {
      if (!line.trim()) return <div key={i} className="h-2" />;
      const parts = line.split(/\*\*(.*?)\*\*/g);
      const rendered = parts.map((p, j) => j % 2 === 1 ? <strong key={j} className="text-navy-900 font-semibold">{p}</strong> : p);
      if (line.trim().startsWith("•")) {
        return (
          <div key={i} className="flex items-start gap-2 ml-2 mb-1">
            <span className="text-gold-500 mt-1.5 shrink-0">•</span>
            <p className="text-slate-600 text-sm leading-relaxed">{rendered}</p>
          </div>
        );
      }
      // Numbered steps
      if (/^\d+\.\s/.test(line.trim())) {
        return (
          <div key={i} className="flex items-start gap-2 ml-2 mb-1">
            <span className="text-navy-900 font-bold text-sm shrink-0 mt-0.5">{line.match(/^\d+/)[0]}.</span>
            <p className="text-slate-600 text-sm leading-relaxed">{rendered}</p>
          </div>
        );
      }
      return <p key={i} className="text-slate-600 text-sm leading-relaxed mb-1">{rendered}</p>;
    });
  };

  return (
    <div className="border border-slate-200 rounded-2xl overflow-hidden mb-4 shadow-sm">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 bg-white hover:bg-slate-50 transition-colors text-left"
      >
        <h2 className="font-bold text-navy-900 text-base">{s.title}</h2>
        {open ? <ChevronUp size={16} className="text-slate-400 shrink-0" /> : <ChevronDown size={16} className="text-slate-400 shrink-0" />}
      </button>
      {open && (
        <div className="px-6 pb-6 pt-2 bg-white border-t border-slate-100 space-y-1">
          {renderContent(s.content)}
        </div>
      )}
    </div>
  );
}

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* Nav */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full overflow-hidden border border-slate-200">
              <img src={logo} alt="TinyGlobal" className="w-full h-full object-contain" />
            </div>
            <span className="font-bold text-navy-900">TinyGlobal</span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy-900 transition-colors font-medium">
            <ArrowLeft size={14} /> Back to Home
          </Link>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-navy-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gold-500/20 border border-gold-500/30 flex items-center justify-center">
              <FileText size={20} className="text-gold-400" />
            </div>
            <span className="text-gold-400 text-xs font-bold uppercase tracking-widest">Legal</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold mb-3">Terms of Service</h1>
          <p className="text-slate-400 text-sm">
            Effective Date: {EFFECTIVE_DATE} &nbsp;·&nbsp; {COMPANY} (ABN {ABN})
          </p>
          <p className="text-slate-300 text-sm mt-3 max-w-2xl leading-relaxed">
            Please read these Terms carefully before using the TinyGlobal Platform. These Terms are governed by the laws of New South Wales, Australia. By using the Platform, you agree to be bound by these Terms.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {sections.map(s => <Section key={s.id} s={s} />)}

        {/* Contact card */}
        <div className="mt-8 p-6 bg-navy-900 rounded-2xl text-white">
          <div className="flex items-start gap-4">
            <Mail size={20} className="text-gold-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold mb-1">Questions about these Terms?</p>
              <p className="text-slate-300 text-sm">Contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-gold-400 hover:text-gold-300 transition-colors">{CONTACT_EMAIL}</a></p>
              <p className="text-slate-400 text-xs mt-2">{COMPANY} · ABN {ABN} · Sydney NSW · Built by Yadata</p>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-8 pb-8">
          © {new Date().getFullYear()} {COMPANY} ABN {ABN} · <Link to="/privacy" className="hover:text-navy-900 transition-colors">Privacy Policy</Link> · <Link to="/" className="hover:text-navy-900 transition-colors">Home</Link>
        </p>
      </div>
    </div>
  );
}
